import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  TextField,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { IApiFunc, IApiResult } from 'auth/interface/api.interface'
import { useApi } from 'auth/useApi3'
import ClpTable from 'components/ClpTable'
import TableButton from 'components/ClpTable/components/actions/TableButton/TableButton'
import { StyledTableButtonGroup } from 'components/ClpTable/components/actions/TableButton/tableButtonGroup.styled'
import { TableButtonTypeEnum } from 'components/ClpTable/components/actions/TableButton/tableButtonProps.interface'
import ContinueDialog from 'components/Dialogs/ContinueDialog'
import { Field, Form, Formik } from 'formik'
import { apiEndpoints } from 'generated/apiEndpoints'
import React, { useEffect, useMemo, useState } from 'react'
import { Vehicle, VehicleGroup, VehicleGroupFormData, VehicleGroupsTabProps } from './VehicleGroupsTab.interfaces'
import { StyledContainer, StyledDialog, StyledDialogContent, StyledDialogTitle, StyledFormField, StyledVehicleCard, StyledVehicleList } from './VehicleGroupsTab.styled'
import { validationSchema } from './VehicleGroupsTab.validation'
import { useTranslation } from 'react-i18next'
import DefaultButton from 'components/Button'
import { StyledHeaderContainer, StyledTableContainer } from 'shared/styled/Global'
import { DynamicTooltip } from 'components/Tooltip/DynamicTooltip'
import ChipTooltip from 'components/ChipTooltip/ChipTooltip'
import { StyledButtonContainer } from '../CreateTemplateModal/CreateTemplateModal.styled'
import { StyledInputLabel } from 'components/Form/FormComponents/FormSwitch/FormSwitch.styled'
import { SnackbarState } from '../CheckTemplates/CheckTemplates.interfaces'
import SuccessDialog from 'components/Dialogs/SuccessDialog'
import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const tableDesc = `Skapa grupper av fordon för att enklare hantera gemensamma inställningar.<br />En fordonsgrupp kan kopplas till <strong>egna fält</strong> och <strong>kontrollmallar</strong>, vilket gör det smidigt att tillämpa samma regler eller information på flera fordon samtidigt.<br />Ange ett <strong>gruppnamn</strong> och välj vilka fordon som ska ingå i gruppen.`

const VehicleGroupsTab: React.FC<VehicleGroupsTabProps> = ({ onGroupsChange }) => {
  const theme = useTheme()
  const [groups, setGroups] = useState<VehicleGroup[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<VehicleGroup | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [showInternalNumber, setShowInternalNumber] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<VehicleGroup | null>(null)
  const [vehiclesResult, vehiclesApi] = useApi() as [IApiResult, IApiFunc]
  const [groupsResult, groupsApi] = useApi() as [IApiResult, IApiFunc]
  const [upsertGroupResult, upsertGroupApi] = useApi() as [IApiResult, IApiFunc]
  const [deleteGroupResult, deleteGroupApi] = useApi() as [IApiResult, IApiFunc]
  const { t } = useTranslation()
  const [showDialog, setShowDialog] = useState(false)
  const [vehicleSearch, setVehicleSearch] = useState("");

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.toLowerCase().trim();

    if (!q) return vehicles;

    return vehicles.filter((v: any) =>
      [
        v.regNumber,
        v.internnumber,
        v.brand,
        v.model,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    );
  }, [vehicles, vehicleSearch]);

  const snackBarErrorFetch: SnackbarState = {
    title: t("dashboard.something_went_wrong", "Something went wrong"),
    message: t("checks.failed_checktemplate_details", "Failed to retrieve check template details"),
  }

  const snackBarSuccess: SnackbarState = {
    title: selectedGroup ? t("global.done_dialog", "Done!") : t("global.done_dialog", "Done!"),
    message: selectedGroup ? t("checks.vehicle_group_updated", "Vehicle group updated") : t("checks.vehicle_group_created", "Vehicle group created"),
  }
  const [snackbar, setSnackbar] = useState<SnackbarState>(snackBarSuccess)

  useEffect(() => {
    vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)
    groupsApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
  }, [vehiclesApi, groupsApi])

  useEffect(() => {
    if (vehiclesResult.status === 2 && vehiclesResult.data) {
      setVehicles(vehiclesResult.data)
    }
  }, [vehiclesResult])

  useEffect(() => {
    if (groupsResult.status === 2 && groupsResult.data) {
      const mappedGroups = groupsResult.data.map((group: any) => ({
        id: group.id,
        name: group.name,
        vehicleIds: group.vehiclesInfo ? group.vehiclesInfo.map((v: any) => v.id) : [],
        vehiclesInfo: group.vehiclesInfo || []
      }))
      setGroups(mappedGroups)
      if (onGroupsChange) {
        onGroupsChange(mappedGroups)
      }
    }
  }, [groupsResult, onGroupsChange])

  useEffect(() => {
    if (upsertGroupResult.status !== 2) return
    setSnackbar(snackBarSuccess)
    setShowDialog(true)
    groupsApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
    handleCloseModal()
  }, [upsertGroupResult, groupsApi])



  useEffect(() => {
    if (deleteGroupResult.status !== 2) return
    groupsApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldgroups)
    setGroupToDelete(null)
  }, [deleteGroupResult, groupsApi])

  const handleCreateGroup = () => {
    setSelectedGroup(null)
    setModalOpen(true)
  }

  const handleEditGroup = (group: VehicleGroup) => {
    setSelectedGroup(group)
    setModalOpen(true)
  }

  const handleDeleteGroup = (group: VehicleGroup) => {
    setGroupToDelete(group)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!groupToDelete) return
    const endpoint = apiEndpoints.vehicleregistryadmin.deletecustomfieldgroup.replace('{customFieldGroupId}', groupToDelete.id.toString())
    deleteGroupApi.del(endpoint)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedGroup(null)
  }

  const handleSubmit = (values: VehicleGroupFormData) => {
    const upsertDto = {
      id: selectedGroup?.id || 0,
      name: values.name,
      customFieldNames: [], // We don't use custom fields for vehicle check templates
      vehiclesInfo: values.selectedVehicles.map(vehicleId => {
        const vehicle = vehicles.find(v => v.id === vehicleId)
        return {
          id: vehicleId,
          regNumber: vehicle?.regNumber || '',
          internnumber: vehicle?.internnumber || ''
        }
      })
    }

    const url = apiEndpoints.vehicleregistryadmin.upsertcustomfieldgroup
    upsertGroupApi.put(url, upsertDto)
  }

  const columns = [
    {
      accessorKey: 'name',
      header: t("global.group_name", "Group name"),
    },
    {
      accessorKey: 'vehicleCount',
      header: t("global.vehicle_count", "Vehicle count"),
      cell: ({ row }: any) => row.original.vehicleIds.length,
    },
    {
      accessorKey: 'vehicles',
      header: t("global.vehicle", "Vehicles"),
      cell: ({ row }: any) => {
        const vehiclesInfo = row.original.vehiclesInfo
        const visible = vehiclesInfo.slice(0, 3);
        const remaining = vehiclesInfo.slice(3);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: { xs: "flex-end", lg: "flex-start" } }}>
            {visible.map((vehicle: Vehicle) => (
              <Chip
                key={vehicle.id}
                label={vehicle.regNumber}
                size="small"
                variant="outlined"
              />
            ))}

            {remaining.length > 0 && (
              <ChipTooltip
                label={`+${remaining.length} till`}
                remaining={remaining.map((v: any) => v.regNumber).join(", ")}
              />
            )}
          </Box>
        )
      },
    },
    {
      accessorKey: 'actions',
      header: '',
      align: 'right',
      cell: ({ row }: any) => (
        <StyledTableButtonGroup>
          <TableButton
            type={TableButtonTypeEnum.Edit}
            handleOnClick={() => handleEditGroup(row.original)}
          />
          <TableButton
            type={TableButtonTypeEnum.Delete}
            handleOnClick={() => handleDeleteGroup(row.original)}
          />
        </StyledTableButtonGroup>
      ),
    },
  ]

  const initialFormValues: VehicleGroupFormData = {
    name: selectedGroup?.name || '',
    selectedVehicles: selectedGroup?.vehicleIds || [],
  }

  return (
    <StyledContainer>
      <StyledHeaderContainer>
        <Box>
          <Typography variant="h1">{t("global.vehicle_groups", "Vehicle groups")}</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateGroup}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t("global.create_group", "Create group")}
        </Button>
      </StyledHeaderContainer>

      <StyledTableContainer>
        <Box display="flex" alignItems="center" gap={1} px={{ xs: 2, sm: 3 }} pt={3}>
          <Typography variant="labelSmall">{t("global.information", "Information")}</Typography>
          <DynamicTooltip t={t} title={t("global.vehicle_groups", "Vehicle groups")}>
            <Grid item>
              <Typography fontWeight="600" variant="labelSmall">
                {t("vehicleregistry.how_to_use_vehicle_groups", "How to use vehicle groups")}
              </Typography>
              <Typography
                sx={{ mt: 0.5 }}
                variant="bodySmall"
                color="textSecondary"
                dangerouslySetInnerHTML={{
                  __html: t("checks.group_table_desc", tableDesc)
                }}
              />
            </Grid>
          </DynamicTooltip>
        </Box>
        <ClpTable
          columns={columns}
          rows={groups}
          loadingState={groupsResult.status}
          noRowsErrorText={t("checks.no_groups_exists", 'No vehicle groups created yet. Click "Create Group" to get started.')}
        />
      </StyledTableContainer>


      <StyledDialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
      >
        <StyledDialogTitle>
          <Typography variant="h3">
            {selectedGroup ? t("checks.edit_vehicle_group", "Edit vehiclegroup") : t("checks.create_vehicle_group", "Create vehicle group")}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <Formik
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <StyledDialogContent>
                <Box>
                  <StyledInputLabel>
                    <Typography variant="h6" color="text.primary">
                      {t("global.group_name", "Group name")}
                    </Typography>
                  </StyledInputLabel>
                  <Field
                    as={StyledFormField}
                    name="name"
                    fullWidth
                    variant="outlined"
                    error={formik.touched.name && !!formik.errors.name}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </Box>

                <Box mb={2}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexDirection={{ xs: "column", sm: "row" }}>
                    <Typography variant="h3">
                      {t("global.validation.choose_included_vehicles", "Choose vehicles to include")}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="labelMedium">
                          {t("global.internal_number_short", "Internal No")}
                        </Typography>
                        <Radio
                          checked={showInternalNumber}
                          onChange={(e) => setShowInternalNumber(true)}
                        />
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Typography variant="labelMedium">
                          {t("global.reg_number_short", "Reg no")}
                        </Typography>
                        <Radio
                          checked={!showInternalNumber}
                          onChange={(e) => setShowInternalNumber(false)}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Grid container justifyContent="space-between" alignItems="center" gap={1} mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>

                      <FormControlLabel
                        sx={{
                          '& .MuiFormControlLabel-label': (theme) => ({
                            fontSize: theme.typography.labelMedium.fontSize,
                          }),
                          minWidth: "120px"
                        }}
                        control={
                          <Checkbox
                            sx={{
                              color: (theme.palette as any).primary[800],
                              "&.Mui-checked": {
                                color: (theme.palette as any).primary[800]
                              },
                              "&.MuiCheckbox-indeterminate": {
                                color: (theme.palette as any).primary[800],
                              },
                            }}
                            indeterminate={
                              formik.values.selectedVehicles.length > 0 &&
                              formik.values.selectedVehicles.length < vehicles.length
                            }
                            checked={
                              vehicles.length > 0 &&
                              formik.values.selectedVehicles.length === vehicles.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                formik.setFieldValue('selectedVehicles', vehicles.map(v => v.id))
                              } else {
                                formik.setFieldValue('selectedVehicles', [])
                              }
                            }}
                          />
                        }
                        label={t("global.choose_all", "Choose all")}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t("global.search_vehicle", "Search vehicle")}
                        value={vehicleSearch}
                        onChange={(e) => setVehicleSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FontAwesomeIcon icon={faSearch} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box display="flex" mr={2} gap={0.5}>
                      <Typography variant="h5" component="span">
                        {formik.values.selectedVehicles.length}
                      </Typography>
                      <Typography component="span">
                        {t("global.vehcles_picked", "vehicles picked")?.toLowerCase()}
                      </Typography>
                    </Box>
                  </Grid>

                  {vehiclesResult.status === 1 ? (
                    <Typography>{t("global.validation.loading_vehicles", "Loading vehicles")}...</Typography>
                  ) : (
                    <StyledVehicleList>
                      <Grid container spacing={1}>
                        {filteredVehicles.map((vehicle) => (
                          <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                            <Paper
                              sx={{
                                p: 2,
                                cursor: "pointer",
                                boxShadow: "none",
                                borderRadius: "12px",
                                border: formik.values.selectedVehicles.includes(vehicle.id)
                                  ? `2px solid ${(theme.palette as any).primary[800]}`
                                  : `1px solid ${theme.palette.divider}`,
                                backgroundColor: formik.values.selectedVehicles.includes(vehicle.id)
                                  ? theme.palette.primary.light + "20"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                              onClick={() => {
                                const isSelected = formik.values.selectedVehicles.includes(vehicle.id)
                                if (isSelected) {
                                  formik.setFieldValue(
                                    'selectedVehicles',
                                    formik.values.selectedVehicles.filter(id => id !== vehicle.id)
                                  )
                                } else {
                                  formik.setFieldValue(
                                    'selectedVehicles',
                                    [...formik.values.selectedVehicles, vehicle.id]
                                  )
                                }
                              }}
                            >
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  checked={formik.values.selectedVehicles.includes(vehicle.id)}
                                  onChange={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ p: 0, mr: 1 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="bodySmall" fontWeight="medium">
                                    {showInternalNumber
                                      ? (vehicle.internnumber || t("global.no_intern_number", "No internal number"))
                                      : vehicle.regNumber
                                    }
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </StyledVehicleList>
                  )}
                </Box>

                {formik.touched.selectedVehicles && formik.errors.selectedVehicles && (
                  <Typography
                    variant="bodySmall"
                    color="error"
                    sx={{ mt: 2 }}
                  >
                    {formik.errors.selectedVehicles}
                  </Typography>
                )}

                <StyledButtonContainer>
                  <Button onClick={handleCloseModal} variant="outlined">
                    {t("global.cancel", "Cancel")}
                  </Button>
                  <DefaultButton
                    result={upsertGroupResult}
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    {selectedGroup ? t("global.save_changes", "Save changes") : t("global.create_group", "Create group")}
                  </DefaultButton>
                </StyledButtonContainer>
              </StyledDialogContent>
            </Form>
          )}
        </Formik>
      </StyledDialog>
      {showDialog && (
        <SuccessDialog
          resetModalState={setShowDialog}
          message={snackbar.message ?? ""}
          title={snackbar.title ?? ""}
        />
      )}
      {deleteDialogOpen && groupToDelete && (
        <ContinueDialog
          onAddSubmit={handleDeleteConfirm}
          setShowContinueModal={setDeleteDialogOpen}
          result={deleteGroupResult}
          cancelBtnText={t("global.buttons.cancel", "Cancel")}
          continueBtnText={t("global.delete", "Delete")}
          isOpen={deleteDialogOpen}
        >
          <Typography align="center" variant="h4" paragraph>
            {t("checks.delete_vehicle_group", "Delete vehicle group")}
          </Typography>
          <Typography
            sx={{ mb: 0 }}
            align="center"
            paragraph
            dangerouslySetInnerHTML={{
              __html: t("checks.confirm_delete_vehicle_group", `Are you sure you want to delete the vehicle group <strong>{{ name }}</strong>?<br />The group contains <strong>{{ count }} vehicles</strong>.`, { name: groupToDelete?.name, count: groupToDelete?.vehicleIds.length || 0 })
            }}
          />
        </ContinueDialog>
      )}
    </StyledContainer>
  )
}

export default VehicleGroupsTab
